import * as z from 'zod';
import { createSelectSchema } from 'drizzle-zod';
import type { QueryResult } from '@iichi/schema-database/types/item';
import * as schema from '@iichi/schema-database';

const itemI18NSchema = createSelectSchema(schema.itemI18N).pick({
  title: true,
  description: true,
  aboutShipping: true,
  craftPeriod: true,
  aboutCraftPeriod: true,
  remark: true,
});

const itemListingFirstLastHistorySchema = createSelectSchema(schema.itemListingFirstLastHistory)
  .pick({
    firstListedAt: true,
    lastListedAt: true,
  })
  .extend({
    firstListedAt: z.coerce.date(),
    lastListedAt: z.coerce.date(),
  });

/**
 * 商品の基本情報スキーマ
 * データベース層のitemSelectSchemaから必要なフィールドのみを選択
 */
export const itemSchema = createSelectSchema(schema.item)
  .pick({
    itemId: true,
    shopId: true,
    categoryId: true,
    price: true,
    stock: true,
    published: true,
    deleted: true,
    publishedAt: true,
    likeCount: true,
    isGift: true,
    isOrdermade: true,
    createdAt: true,
    updatedAt: true,
  })
  .extend({
    // 型の調整
    published: z.coerce.boolean(),
    deleted: z.coerce.boolean(),
    isGift: z.coerce.boolean(),
    isOrdermade: z.coerce.boolean(),
    publishedAt: z.coerce.date().optional(),
    createdAt: z.coerce.date(),
    updatedAt: z.coerce.date(),
  })
  .extend({
    // item_i18nのフィールドを追加
    title: itemI18NSchema.shape.title,
    description: itemI18NSchema.shape.description,
    aboutShipping: itemI18NSchema.shape.aboutShipping,
    craftPeriod: itemI18NSchema.shape.craftPeriod,
    aboutCraftPeriod: itemI18NSchema.shape.aboutCraftPeriod,
    remark: itemI18NSchema.shape.remark,
  })
  .extend({
    // item_listing_first_last_historyのフィールドを追加
    firstListedAt: itemListingFirstLastHistorySchema.shape.firstListedAt.optional(),
    lastListedAt: itemListingFirstLastHistorySchema.shape.lastListedAt.optional(),
  });

export type Item = z.infer<typeof itemSchema>;

export const transformItem = (data: QueryResult): Item => {
  const item = {
    ...data,
    createdAt: data.createdAt,
    aboutCraftPeriod: data.itemI18Ns?.[0]?.aboutCraftPeriod ?? '',
    aboutShipping: data.itemI18Ns?.[0]?.aboutShipping ?? '',
    craftPeriod: data.itemI18Ns?.[0]?.craftPeriod ?? '',
    description: data.itemI18Ns?.[0]?.description ?? '',
    remark: data.itemI18Ns?.[0]?.remark ?? '',
    title: data.itemI18Ns?.[0]?.title ?? '',
    firstListedAt: data.itemListingFirstLastHistory?.firstListedAt,
    lastListedAt: data.itemListingFirstLastHistory?.lastListedAt,
  };

  return itemSchema.parse(item);
};
